import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  financeService,
  FixedExpenseCategoryCreate,
  FixedExpenseCategoryUpdate,
  FixedExpenseCreate,
  FixedExpenseUpdate,
  ExpenseFilters,
  CopyRecurringExpensesRequest
} from '@/services/finance';

// === Hooks de Categorías ===

export function useExpenseCategories(includeInactive = false) {
  return useQuery({
    queryKey: ['finance', 'categories', includeInactive],
    queryFn: () => financeService.getCategories(includeInactive),
  });
}

export function useExpenseCategory(id: number | null) {
  return useQuery({
    queryKey: ['finance', 'category', id],
    queryFn: () => financeService.getCategory(id!),
    enabled: !!id,
  });
}

export function useCreateExpenseCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FixedExpenseCategoryCreate) => financeService.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'categories'] });
    },
  });
}

export function useUpdateExpenseCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: FixedExpenseCategoryUpdate }) =>
      financeService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'expenses'] });
    },
  });
}

export function useDeleteExpenseCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => financeService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'categories'] });
    },
  });
}

// === Hooks de Gastos ===

export function useExpenses(filters?: ExpenseFilters) {
  return useQuery({
    queryKey: ['finance', 'expenses', filters],
    queryFn: () => financeService.getExpenses(filters),
  });
}

export function useExpense(id: number | null) {
  return useQuery({
    queryKey: ['finance', 'expense', id],
    queryFn: () => financeService.getExpense(id!),
    enabled: !!id,
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FixedExpenseCreate) => financeService.createExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'expenses'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'summary'] });
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: FixedExpenseUpdate }) =>
      financeService.updateExpense(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'expenses'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'summary'] });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => financeService.deleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'expenses'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'summary'] });
    },
  });
}

export function useToggleExpensePaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => financeService.togglePaid(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'expenses'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'summary'] });
    },
  });
}

// === Hooks de Resúmenes ===

export function useMonthlyExpenseSummary(year: number, month: number) {
  return useQuery({
    queryKey: ['finance', 'summary', 'expenses', year, month],
    queryFn: () => financeService.getMonthlyExpenseSummary(year, month),
  });
}

export function useMonthlyFinancialSummary(year: number, month: number) {
  return useQuery({
    queryKey: ['finance', 'summary', 'financial', year, month],
    queryFn: () => financeService.getMonthlyFinancialSummary(year, month),
  });
}

export function useMonthlyCashFlow(year: number, month: number) {
  return useQuery({
    queryKey: ['finance', 'cash-flow', year, month],
    queryFn: () => financeService.getMonthlyCashFlow(year, month),
  });
}

export function useYearlyFinancialSummary(year: number) {
  return useQuery({
    queryKey: ['finance', 'summary', 'yearly', year],
    queryFn: () => financeService.getYearlyFinancialSummary(year),
  });
}

export function useCopyRecurringExpenses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CopyRecurringExpensesRequest) => financeService.copyRecurringExpenses(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'expenses'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'summary'] });
    },
  });
}

// === Hooks de Desgloses Detallados ===

export function useProductCostBreakdown(year: number, month?: number, enabled = true) {
  return useQuery({
    queryKey: ['finance', 'breakdown', 'products', year, month],
    queryFn: () => financeService.getProductCostBreakdown(year, month),
    enabled,
  });
}

export function useSalesBreakdown(year: number, month?: number, enabled = true) {
  return useQuery({
    queryKey: ['finance', 'breakdown', 'sales', year, month],
    queryFn: () => financeService.getSalesBreakdown(year, month),
    enabled,
  });
}

export function useWithdrawalBreakdown(year: number, month: number) {
  return useQuery({
    queryKey: ['finance', 'withdrawal-breakdown', year, month],
    queryFn: () => financeService.getWithdrawalBreakdown(year, month),
  });
}

export function useWithdrawalsDetail(year: number, month: number) {
  return useQuery({
    queryKey: ['finance', 'withdrawals-detail', year, month],
    queryFn: () => financeService.getWithdrawalsDetail(year, month),
  });
}
